import React, { useImperativeHandle, forwardRef, useState, useRef, useEffect } from 'react';
import { Box, IconButton, Button, Tooltip } from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import UndoIcon from '@mui/icons-material/Undo';
import BrushIcon from '@mui/icons-material/Brush';
import FormatColorFillIcon from '@mui/icons-material/FormatColorFill';
import SaveIcon from '@mui/icons-material/Save';

const FeedbackAnnotation = forwardRef(({
  screenshot = null,
  activeColor = 'red',
  setActiveColor = () => {},
  onRetake = () => {},
  colorOptions = [],
  onSave = () => {}
}, ref) => {
  const [rectangles, setRectangles] = useState([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPoint, setStartPoint] = useState({ x: 0, y: 0 });
  const [currentRect, setCurrentRect] = useState(null);
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const imageRef = useRef(null);
  
  // Set up the canvas when screenshot changes
  useEffect(() => {
    if (!screenshot || !canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Load the image to get dimensions
    const img = new Image();
    img.src = screenshot;
    img.onload = () => {
      // Set canvas dimensions to match image
      canvas.width = img.width;
      canvas.height = img.height;
      imageRef.current = img;
      
      // Draw the image
      ctx.drawImage(img, 0, 0);
      
      // Draw any existing rectangles
      drawAllRectangles();
    };
  }, [screenshot, rectangles]);
  
  // Draw all rectangles on the canvas
  const drawAllRectangles = () => {
    if (!canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Clear canvas and redraw image
    if (imageRef.current) {
      ctx.drawImage(imageRef.current, 0, 0);
    }
    
    // Draw all saved rectangles
    rectangles.forEach(rect => {
      ctx.strokeStyle = rect.color;
      ctx.lineWidth = 3;
      ctx.strokeRect(rect.x, rect.y, rect.width, rect.height);
    });
    
    // Draw current rectangle if exists
    if (currentRect) {
      ctx.strokeStyle = activeColor;
      ctx.lineWidth = 3;
      ctx.strokeRect(currentRect.x, currentRect.y, currentRect.width, currentRect.height);
    }
  };
  
  // Handle mouse down to start drawing
  const handleMouseDown = (e) => {
    if (!canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    setStartPoint({ x, y });
    setCurrentRect({ x, y, width: 0, height: 0, color: activeColor });
    setIsDrawing(true);
  };
  
  // Handle mouse move to update current rectangle
  const handleMouseMove = (e) => {
    if (!isDrawing || !canvasRef.current || !currentRect) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    const updatedRect = {
      x: Math.min(x, startPoint.x),
      y: Math.min(y, startPoint.y),
      width: Math.abs(x - startPoint.x),
      height: Math.abs(y - startPoint.y),
      color: activeColor
    };
    
    setCurrentRect(updatedRect);
    drawAllRectangles();
  };
  
  // Handle mouse up to finish drawing
  const handleMouseUp = () => {
    if (!isDrawing || !currentRect) return;
    
    // Add current rectangle to the list
    setRectangles(prev => [...prev, { ...currentRect }]);
    setIsDrawing(false);
    setCurrentRect(null);
  };
  
  // Undo the last rectangle
  const handleUndo = () => {
    setRectangles(prev => prev.slice(0, -1));
  };
  
  // Create a manual save function
  const handleManualSave = async () => {
    console.log('Manual save triggered');
    try {
      if (!canvasRef.current) {
        console.error('Canvas not available');
        return null;
      }
      
      // Make sure all rectangles are drawn
      drawAllRectangles();
      
      // Convert canvas to data URL
      const dataUrl = canvasRef.current.toDataURL('image/png');
      console.log('Save completed manually with result');
      
      // Call the onSave callback
      if (typeof onSave === 'function') {
        onSave(dataUrl);
      }
      
      return dataUrl;
    } catch (error) {
      console.error('Manual save error:', error);
      return null;
    }
  };
  
  // Expose save method to parent
  useImperativeHandle(ref, () => ({
    save: handleManualSave
  }), [handleManualSave]);
  
  if (!screenshot) return null;
  
  return (
    <Box sx={{ width: 'auto', maxWidth: '100%', height: 'auto', overflow: 'visible' }} ref={containerRef}>
      <Box 
        sx={{ 
          display: 'flex',
          justifyContent: 'space-between',
          mb: 1,
          flexWrap: 'wrap',
          gap: 1,
          position: 'sticky',
          top: 0,
          zIndex: 10,
          bgcolor: 'white',
          py: 1
        }}
      >
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip>
            <Box sx={{ position: 'relative' }}>
              <IconButton 
                sx={{ 
                  bgcolor: activeColor,
                  '&:hover': { bgcolor: activeColor },
                  width: 36,
                  height: 36
                }}
              >
                <FormatColorFillIcon sx={{ color: 'white' }} />
              </IconButton>
              <Box 
                sx={{ 
                  position: 'absolute', 
                  top: '100%', 
                  left: 0, 
                  zIndex: 1000,
                  display: 'flex',
                  gap: 0.5,
                  mt: 0.5,
                  p: 0.5,
                  bgcolor: 'background.paper',
                  boxShadow: 3,
                  borderRadius: 1
                }}
              >
                {colorOptions.map((option) => (
                  <Tooltip key={option.color} title={option.label}>
                    <Box
                      onClick={() => setActiveColor(option.color)}
                      sx={{ 
                        width: 24, 
                        height: 24, 
                        bgcolor: option.color,
                        cursor: 'pointer',
                        border: activeColor === option.color ? '2px solid black' : '1px solid #ccc',
                        borderRadius: 0.5
                      }}
                    />
                  </Tooltip>
                ))}
              </Box>
            </Box>
          </Tooltip>
          <Tooltip title="Draw rectangle">
            <IconButton color="primary">
              <BrushIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Undo">
            <IconButton onClick={handleUndo} color="primary">
              <UndoIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            color="success"
            startIcon={<SaveIcon />}
            sx={{ fontWeight: 'bold' }}
            onClick={handleManualSave}
          >
            Save Annotations
          </Button>
          <Button
            variant="outlined"
            startIcon={<PhotoCameraIcon />}
            onClick={onRetake}
          >
            Retake Screenshot
          </Button>
        </Box>
      </Box>
      <Box 
        sx={{ 
          border: '1px solid #ccc', 
          borderRadius: 1,
          overflow: 'visible',
          width: 'auto',
          height: 'auto',
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'flex-start',
          position: 'relative'
        }}
      >
        <canvas
          ref={canvasRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          style={{ 
            display: 'block', 
            width: 'auto',
            height: 'auto',
            cursor: 'crosshair'
          }}
        />
      </Box>
    </Box>
  );
});

export default FeedbackAnnotation; 